import { WithDisposable } from '@blocksuite/global/lit';
import { CenterPeekIcon, MoreHorizontalIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css, html, nothing } from 'lit';
import { property } from 'lit/decorators.js';

/**
 * View-agnostic row operations cluster — the hover-reveal pair of
 * "open detail" (CenterPeek) + "more" (MoreHorizontal) buttons that
 * both Table and Gantt rows surface at the end of their title cell.
 *
 * Consumers supply:
 *   - `onPeek(target)`  → opens the row detail panel
 *   - `onMore(target)`  → pops the row context menu (Expand / Insert /
 *                         Delete). Optional; when omitted the "…"
 *                         button is hidden, which matches the
 *                         Table readonly path.
 *
 * The element intentionally stays dumb: no logic about selection,
 * readonly, or menu contents. Hosts decide what pops up.
 */
export class DvRowOps extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    dv-row-ops {
      display: inline-flex;
      flex-shrink: 0;
    }
    .dv-row-ops {
      display: flex;
      gap: 4px;
      align-items: center;
      justify-content: end;
      cursor: pointer;
      flex-shrink: 0;
    }
    /* isolation creates a stacking context so the :before hover
       overlay can sit on its own layer without painting on top of
       the icon. Without it, positioned :before paints AFTER in-flow
       SVG content and blanks the icon on hover — visible in
       blue-white theme where hover-color is near-white. */
    .dv-row-op {
      display: flex;
      padding: 4px;
      border-radius: 4px;
      box-shadow: var(--streamlain-button-shadow);
      background-color: var(--streamlain-background-primary-color);
      position: relative;
      flex-shrink: 0;
      isolation: isolate;
    }
    .dv-row-op:hover:before {
      content: '';
      border-radius: 4px;
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background-color: var(--streamlain-hover-color);
      z-index: -1;
    }
    /* 8 px gap from the right edge of the overflow-cluster. Lives on
       the last child (not on the outer host) so Table's width:0
       floating slot doesn't shift following cells by 8 px — inner
       margin only offsets content, not the flex-item's layout box. */
    .dv-row-op:last-child {
      margin-right: 8px;
    }
    .dv-row-op svg {
      fill: var(--streamlain-icon-color);
      color: var(--streamlain-icon-color);
      width: 16px;
      height: 16px;
    }
  `;

  @property({ attribute: false })
  accessor onPeek: ((target: HTMLElement) => void) | undefined;

  @property({ attribute: false })
  accessor onMore: ((target: HTMLElement) => void) | undefined;

  private readonly handlePeek = (e: MouseEvent): void => {
    e.stopPropagation();
    this.onPeek?.(e.currentTarget as HTMLElement);
  };

  private readonly handleMore = (e: MouseEvent): void => {
    e.stopPropagation();
    this.onMore?.(e.currentTarget as HTMLElement);
  };

  override render() {
    return html`<div
      class="dv-row-ops"
      @pointerdown=${(e: PointerEvent) => e.stopPropagation()}
    >
      <div class="dv-row-op" @click=${this.handlePeek}>${CenterPeekIcon()}</div>
      ${this.onMore
        ? html`<div class="dv-row-op" @click=${this.handleMore}>
            ${MoreHorizontalIcon()}
          </div>`
        : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-row-ops': DvRowOps;
  }
}
